import ROUTE_NAMES from '../../Widgets/ModalWidget/Routes/constants';
import { FLOATING_WIDGET_TYPE } from '../../Contracts/WidgetTypes/FloatingWidgetType';
import { BOOK_NOW_WIDGET_TYPE } from '../../Contracts/WidgetTypes/BookNowType';

/**
 * Virtual page context for the second PostHog instance.
 *
 * The widget is embedded on arbitrary customer pages, so the real `$current_url` would spread one
 * widget page over thousands of URLs. Like the checkout app (`bkapp/js/insights/bkInsights.js`),
 * every event is instead attributed to a virtual URL that identifies the widget page:
 *
 *   https://widget.bookingkit.com/widget/<widgetType>/<page>
 *
 * The customer's page is kept on the event as `embed_url` / `embed_host`, and attribution query
 * parameters of the customer's URL are copied onto the virtual URL.
 */
export const POSTHOG_V2_VIRTUAL_ORIGIN = 'https://widget.bookingkit.com';
export const POSTHOG_V2_VIRTUAL_PATH_PREFIX = '/widget';

/** Page shown while a widget is mounted but has not navigated yet (its trigger button). */
export const POSTHOG_V2_MOUNTED_PAGE = 'button';

/** PostHog understands `$pageview` natively (Web Analytics, paths); the checkout app maps it the same way. */
export const POSTHOG_V2_EVENT_NAME_MAPPING: Readonly<Record<string, string>> = {
    page_view: '$pageview',
};

const WIDGET_TYPE_SEGMENTS: Readonly<Record<string, string>> = {
    [FLOATING_WIDGET_TYPE]: 'floating_widget',
    [BOOK_NOW_WIDGET_TYPE]: 'book_now',
};

const ROUTE_PAGE_SEGMENTS: Readonly<Record<string, string>> = {
    [ROUTE_NAMES.BUTTON_VIEW]: POSTHOG_V2_MOUNTED_PAGE,
    [ROUTE_NAMES.EXPERIENCE_LIST]: 'list-widget',
    [ROUTE_NAMES.DATE_TIME_SELECTION]: 'date-time-selection',
    [ROUTE_NAMES.RENEW_SEASON_PASS]: 'renew-season-pass',
    [ROUTE_NAMES.CHECKOUT]: 'checkout',
};

const ATTRIBUTION_PARAMS = [
    'ref',
    'bkref',
    'bklink',
    'gclid',
    'fbclid',
    'msclkid',
];
const ATTRIBUTION_PREFIX = 'utm_';

export interface VirtualPage {
    widgetType: string;
    page: string;
}

export type EmbeddingLocation = Pick<Location, 'href' | 'host' | 'search'>;

const toKebabCase = (value: string): string =>
    value
        .replace(/Route$/, '')
        .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
        .toLowerCase();

const isKnownWidgetType = (value: unknown): value is string =>
    typeof value === 'string' && value in WIDGET_TYPE_SEGMENTS;

const asString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.length > 0 ? value : undefined;

/**
 * Maps a widget type to its virtual URL segment. Unknown types fall back to the lower-cased type.
 *
 * @param widgetType - The widget configuration `type`
 * @returns The URL segment for the widget type
 */
export const widgetTypeSegment = (widgetType: string): string =>
    WIDGET_TYPE_SEGMENTS[widgetType] ?? widgetType.toLowerCase();

/**
 * Maps a navigation route to its virtual page. Unknown routes fall back to the kebab-cased name.
 *
 * @param widgetType - The widget configuration `type`
 * @param routeName - The `name` of the widget location
 * @returns The virtual page for the route
 */
export const virtualPageFromRoute = (
    widgetType: string,
    routeName: string
): VirtualPage => ({
    widgetType: widgetTypeSegment(widgetType),
    page: ROUTE_PAGE_SEGMENTS[routeName] ?? toKebabCase(routeName),
});

/**
 * Keeps only attribution parameters (utm_*, ref, bkref, bklink, gclid, fbclid, msclkid) of a query string.
 *
 * @param search - The `location.search` of the embedding page
 * @returns The filtered query string including the leading `?`, or an empty string
 */
export const attributionSearch = (search: string): string => {
    const kept = new URLSearchParams();
    new URLSearchParams(search).forEach((value, key) => {
        const name = key.toLowerCase();
        if (
            name.startsWith(ATTRIBUTION_PREFIX) ||
            ATTRIBUTION_PARAMS.includes(name)
        ) {
            kept.append(key, value);
        }
    });
    const query = kept.toString();
    return query ? `?${query}` : '';
};

/**
 * Builds the virtual URL for a page.
 *
 * @param page - The virtual page
 * @param search - Optional query string to append (already filtered)
 * @returns The absolute virtual URL
 */
export const buildVirtualUrl = (page: VirtualPage, search = ''): URL =>
    new URL(
        `${POSTHOG_V2_VIRTUAL_PATH_PREFIX}/${page.widgetType}/${page.page}${search}`,
        POSTHOG_V2_VIRTUAL_ORIGIN
    );

/**
 * Builds the event properties describing where an event happened: the embedding page and, when
 * known, the virtual widget page as `$current_url` / `$host` / `$pathname`.
 *
 * @param page - The virtual page, or undefined when it is not known
 * @param location - The embedding page location
 * @returns Properties to merge into the event
 */
export const buildVirtualPageProperties = (
    page: VirtualPage | undefined,
    location: EmbeddingLocation
): Record<string, unknown> => {
    const embedding = { embed_url: location.href, embed_host: location.host };
    if (!page) {
        return embedding;
    }
    const url = buildVirtualUrl(page, attributionSearch(location.search));
    return {
        ...embedding,
        $current_url: url.href,
        $host: url.host,
        $pathname: url.pathname,
    };
};

/**
 * Maps widget event names to their PostHog counterparts.
 *
 * @param event - The widget event name
 * @returns The event name to capture
 */
export const mapEventName = (event: string): string =>
    POSTHOG_V2_EVENT_NAME_MAPPING[event] ?? event;

/**
 * Remembers the current virtual page per widget and enriches event properties with it.
 * Enrichment happens at call time so the payload reflects the page at the moment of the event.
 */
export class PosthogV2PageContext {
    private readonly pagesByWidget = new Map<string, VirtualPage>();
    private lastPage: VirtualPage | undefined;

    public constructor(
        private readonly getLocation: () => EmbeddingLocation = () =>
            window.location
    ) {}

    /**
     * Records the page a widget navigated to.
     *
     * @param widgetId - The widget configuration id
     * @param widgetType - The widget configuration type
     * @param routeName - The name of the location the widget navigated to
     * @returns void
     */
    update(widgetId: string, widgetType: string, routeName: string): void {
        const page = virtualPageFromRoute(widgetType, routeName);
        this.pagesByWidget.set(widgetId, page);
        this.lastPage = page;
    }

    /**
     * Adds the page context to event properties. Explicit values in the payload win over derived ones.
     *
     * @param properties - The event properties, if any
     * @returns The enriched properties
     */
    enrich(properties?: Record<string, unknown>): Record<string, unknown> {
        const derived = buildVirtualPageProperties(
            this.resolve(properties),
            this.getLocation()
        );
        return { ...derived, ...(properties ?? {}) };
    }

    private resolve(
        properties?: Record<string, unknown>
    ): VirtualPage | undefined {
        const widgetId =
            asString(properties?.widgetId) ?? asString(properties?.id);
        const knownPage = widgetId
            ? this.pagesByWidget.get(widgetId)
            : undefined;
        if (knownPage) {
            return knownPage;
        }
        // `widget_loaded` carries the widget configuration itself; a freshly mounted widget shows its trigger.
        if (isKnownWidgetType(properties?.type)) {
            return {
                widgetType: widgetTypeSegment(properties.type),
                page: POSTHOG_V2_MOUNTED_PAGE,
            };
        }
        return this.lastPage;
    }
}
